import axios from 'axios';
import { getToken } from '@clerk/react';

const api = axios.create({ baseURL: '/api' });

// getToken() is safe to call from outside React (interceptors, plain JS) --
// it reads the current session off the global Clerk instance, waiting for
// it to finish loading if needed, rather than a hook tied to a component.
// skipCache: every route here is scoped by whichever workspace (personal or
// organization) is active -- see services/authz.js -- and Clerk's ~60s token
// cache would otherwise keep attaching a just-switched-away-from workspace's
// claims to requests made right after switching.
api.interceptors.request.use(async config => {
  const token = await getToken({ skipCache: true });
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const projects = {
  list:   ()         => api.get('/projects'),
  get:    id         => api.get(`/projects/${id}`),
  create: data       => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: id         => api.delete(`/projects/${id}`),
  stop:   id         => api.post(`/projects/${id}/stop`),
};

export const accounts = {
  list:    projectId        => api.get(`/accounts?project_id=${projectId}`),
  log:     id               => api.get(`/accounts/${id}/log`),
  sync:    id               => api.post(`/accounts/${id}/sync`),
  syncAll: projectId        => api.post(`/accounts/project/${projectId}/sync-all`),
  deltaSync: projectId      => api.post(`/accounts/project/${projectId}/delta-sync`),
  create:  (projectId, usernames) => api.post('/accounts', { project_id: projectId, accounts: usernames }),
  update:  (id, data)       => api.put(`/accounts/${id}`, data),
  remove:  id               => api.delete(`/accounts/${id}`),
  stop:    id               => api.post(`/accounts/${id}/stop`),
};

export const reports = {
  get: projectId => api.get(`/reports/${projectId}`),
};

export const jobs = {
  list: projectId => api.get(projectId ? `/jobs?project_id=${projectId}` : '/jobs'),
};

export const apiKeys = {
  list:   ()   => api.get('/api-keys'),
  create: name => api.post('/api-keys', { name }),
  revoke: id   => api.delete(`/api-keys/${id}`),
};

export const webhooks = {
  list:   ()   => api.get('/webhooks'),
  create: url  => api.post('/webhooks', { url }),
  revoke: id   => api.delete(`/webhooks/${id}`),
};

export default api;
